import {
  Button,
  Divider,
  Form,
  Icons,
  InputNumber,
  Select,
  Space,
  Spin,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan } from '@pankod/refine-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ISaleProductState } from 'reducers/product-sale-reducer';
import {
  InventoryLotWithVariant,
  getVariantLotsByWarehouseAndVariantIds,
} from 'services/inventory';
import { SearchIndex, getSearchData } from 'services/meilisearch';
import { IVariant, getVariant } from 'services/variant';
import {
  EDiscountEntryType,
  calculateSaleLineDiscountCost,
  getSaleLineDiscountEntryError,
} from 'utilities/discount-entry';
import { mergedVariantLot } from 'utilities/shared';
import styles from './style.module.css';
import { UnitDiscountFormItem } from './UnitDiscountFormItem';

type AddProductFormProps = {
  warehouseId: string | undefined;
  saleProducts: ISaleProductState[];
  onSubmit: (product: ISaleProductState) => void;
};

type IOption = {
  id: string;
  name: string;
  sku: string;
  quantity: null | number;
};

const { Text } = Typography;
let timeout: ReturnType<typeof setTimeout> | null;
let currentValue: string;

const fetchOptions = (value: string, callback: Function) => {
  if (timeout) {
    clearTimeout(timeout);
    timeout = null;
  }
  currentValue = value;

  const getOptions = async () => {
    const data = await getSearchData(SearchIndex.ProductVariant, value, { limit: 100 });

    if (currentValue === value && value !== '') {
      const options = data
        .map(
          (p) =>
            ({
              id: p.product_variant_id,
              sku: p.sku,
              name: p.name,
              quantity: null,
            }) as IOption,
        )
        .filter((op) => {
          const lowerCasedValue = value.toLowerCase();
          return (
            op.name.toLowerCase().includes(lowerCasedValue) ||
            op.sku.toLowerCase().includes(lowerCasedValue)
          );
        });
      callback(options);
    }
  };

  timeout = setTimeout(getOptions, 300);
};

export const AddProductForm: React.FC<AddProductFormProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<IVariant | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [options, setOptions] = useState<IOption[]>([]);
  const [lots, setLots] = useState<InventoryLotWithVariant[]>([]);
  const [isOptionLoading, setIsOptionLoading] = useState(false);

  const productSelectRef = useRef<any>(null);
  const quantityRef = useRef<any>(null);

  const [form] = Form.useForm();
  const { data: salesPriceDecrease } = useCan({
    resource: 'sales_price',
    action: 'decrease',
  });
  const { data: salesPriceIncrease } = useCan({
    resource: 'sales_price',
    action: 'change',
  });

  const fetchProductsQuantity = useCallback(async (warehouseId: string, ids: string[]) => {
    try {
      const response = await getVariantLotsByWarehouseAndVariantIds(warehouseId, ids);
      const products = mergedVariantLot(response);
      setLots(response);
      return products;
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
      return [];
    }
  }, []);

  const loadInitialOptions = useCallback(async () => {
    try {
      setIsOptionLoading(true);

      const data = await getSearchData(SearchIndex.ProductVariant, '', { limit: 20 });

      const baseOptions: IOption[] = data.map((p) => ({
        id: p.product_variant_id,
        sku: p.sku,
        name: p.name,
        quantity: null,
      }));

      if (props.warehouseId && baseOptions.length) {
        const ids = baseOptions.map((p) => p.id);
        const products = await fetchProductsQuantity(props.warehouseId, ids);

        const productsMap = new Map<string, (typeof products)[0]>();
        products.forEach((p) => productsMap.set(p.id, { ...p }));

        const enrichedOptions = baseOptions.map((option) => {
          const product = productsMap.get(option.id);
          return {
            ...option,
            quantity: product ? product.quantity : 0,
          };
        });

        setOptions(enrichedOptions);
      } else {
        setOptions(baseOptions);
      }
    } catch (err: any) {
      notification.error({
        message: 'Failed to load products',
        description: err.message,
      });
    } finally {
      setIsOptionLoading(false);
    }
  }, [props.warehouseId, fetchProductsQuantity]);

  useEffect(() => {
    loadInitialOptions();
  }, [loadInitialOptions]);

  useEffect(() => {
    // Focus on the product select after rendering the form
    if (!form.getFieldValue('variant')) {
      setTimeout(() => {
        productSelectRef.current?.focus();
      }, 0);
    }
  });

  useEffect(() => {
    form.setFields([
      {
        name: 'variant',
        value: selectedOption?.productVariantId,
      },
      { name: 'unitPrice', value: selectedOption?.sellingPrice },
    ]);
  }, [form, selectedOption]);

  const handleProductOptionSearch = (newValue: string) => {
    setIsOptionLoading(true);

    fetchOptions(newValue, async (data: IOption[]) => {
      setOptions(data);

      if (props.warehouseId && data.length) {
        const ids = data.map((p) => p.id);
        const products = await fetchProductsQuantity(props.warehouseId, ids);

        const productsMap = new Map<string, (typeof products)[0]>();
        products.forEach((p) => productsMap.set(p.id, { ...p }));

        const options = data.map((option) => {
          const product = productsMap.get(option.id);
          return {
            ...option,
            quantity: product ? product.quantity : 0,
          };
        });

        setOptions(options);
      }

      setIsOptionLoading(false);
    });
  };

  const handleProductOptionChange = (newValue: string) => {
    setSearchText(newValue);
  };

  const renderedProductOptions = options.map((p) => {
    // Option is disabled when product has quantity field and quantity is 0 or null
    const color = p.quantity === 0 || p.quantity === null ? '#A9A9A9' : '#626262';
    const title = `${p.sku} - ${p.name} (${p.quantity === null ? '⌛' : p.quantity})`;

    return {
      label: (
        <Typography.Text key={p.id} style={{ color }} title={title}>
          {`${p.sku} - ${p.name}`}{' '}
          {p.quantity === null && <Icons.LoadingOutlined style={{ marginLeft: '0.3rem' }} />}
          {typeof p.quantity === 'number' && `(${p.quantity})`}
        </Typography.Text>
      ),
      value: p.id,
      disabled: p.quantity === null || p.quantity === 0,
    };
  });

  const onSelectVariant = async (id: string) => {
    // Focus on the quantity input after selecting the variant
    setTimeout(() => {
      quantityRef.current?.focus();
    }, 0);

    setIsLoading(true);
    form.resetFields(['quantity']);

    try {
      const variant = await getVariant(id);
      setSelectedOption(variant);
      form.setFields([{ name: 'unitPrice', value: variant.sellingPrice }]);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  const renderedAvailableStock = !selectedOption ? null : (
    <ul>
      {lots
        .filter((lot) => lot.productVariant.id === selectedOption.productVariantId)
        .map((lot) => {
          return lot.quantity > 0 ? (
            <li key={lot.id}>
              <Text>
                {`${lot.quantity} units available | Bought @ BDT ${lot.unitPrice} on
                ${new Date(lot.lotEntryDate).toLocaleDateString('en-GB')}`}
              </Text>
            </li>
          ) : null;
        })}
    </ul>
  );

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = selectedOption?.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const attributeElements = attributes.map((attribute) => (
    <div
      key={attribute.attributeId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
      <Typography.Text>{attribute.value}</Typography.Text>
    </div>
  ));

  const customFields = selectedOption?.customFields ?? [];

  const customFieldElements = customFields.map((field, idx) => {
    const key = Object.keys(field)[0];
    const value = field[key];

    return (
      <div
        key={idx}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  });

  const renderNotFound = () => {
    if (isOptionLoading) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Spin size="small" />
          <Typography.Text style={{ display: 'block', marginTop: 8, color: '#888' }}>
            Searching products...
          </Typography.Text>
        </div>
      );
    }

    return (
      <div style={{ padding: '12px', textAlign: 'center', color: '#888' }}>No products found</div>
    );
  };

  const onFinish = (values: any) => {
    if (!selectedOption) return;

    // Focus on the product select after submitting the form
    setTimeout(() => {
      productSelectRef.current?.focus();
    }, 0);

    const variantId: string = values.variant;
    const unitPrice: number = values.unitPrice;
    const quantity: number = values.quantity;
    const discountEntryValue: number | null = values.discountEntryValue || null;
    const discountEntryType: EDiscountEntryType | null = discountEntryValue
      ? values.discountEntryType
      : null;
    const discountCost = calculateSaleLineDiscountCost(
      quantity,
      unitPrice,
      discountEntryValue,
      discountEntryType,
    );

    const totalQuantity = lots
      .filter((item) => item.productVariant.id === variantId)
      .reduce((acc, lot) => {
        acc += lot.quantity;
        return acc;
      }, 0);

    const isVariantExist = !!props.saleProducts.find(
      (product) => product.productVariantId === variantId,
    );
    if (isVariantExist) {
      form.setFields([
        {
          name: 'variant',
          errors: [`Product is already added.`],
        },
      ]);
      return;
    }

    if (quantity === 0 || quantity > totalQuantity) {
      form.setFields([
        {
          name: 'quantity',
          errors: [`Quantity needs to be between 1 and ${totalQuantity}`],
        },
      ]);
      return;
    }

    // Increase & Decrease the price when have both permissions otherwise
    const canIncreasePrice = salesPriceIncrease?.can;
    const canDecreasePrice = salesPriceDecrease?.can;
    if (canIncreasePrice === false) {
      const isUnitPriceIncreased = unitPrice > selectedOption.sellingPrice;
      // Only increase permission can higher the price
      if (isUnitPriceIncreased) {
        form.setFields([
          {
            name: 'unitPrice',
            errors: ['Price cannot be higher than Sales Price'],
          },
        ]);
        return;
      }
    }

    const discountEntryError = getSaleLineDiscountEntryError(
      unitPrice,
      discountEntryValue,
      discountEntryType,
    );
    if (discountEntryError) {
      form.setFields([{ name: 'discountEntryValue', errors: [discountEntryError] }]);
      return;
    }

    if (canDecreasePrice === false) {
      const isUnitPriceDecreased = unitPrice < selectedOption.sellingPrice;
      // Only decrease permission can lower the price
      if (isUnitPriceDecreased) {
        form.setFields([
          {
            name: 'unitPrice',
            errors: ['Price cannot be lower than Sales Price'],
          },
        ]);
        return;
      }
    }

    props.onSubmit({
      ...selectedOption,
      quantity,
      unitPrice,
      discountCost,
      discountEntryValue,
      discountEntryType,
    });
    form.resetFields();
    setSelectedOption(null);
  };

  return (
    <Form
      layout="vertical"
      initialValues={{ isActive: true, discountEntryType: EDiscountEntryType.AmountPerUnit }}
      form={form}
      onFinish={onFinish}
      autoFocus
    >
      <Form.Item
        label="Product"
        name="variant"
        rules={[{ required: true, message: 'Please select a product' }]}
      >
        <Select
          ref={productSelectRef}
          showSearch
          placeholder="Select a product"
          value={searchText}
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          onSearch={handleProductOptionSearch}
          onChange={handleProductOptionChange}
          onSelect={onSelectVariant}
          options={renderedProductOptions}
          disabled={isLoading}
          notFoundContent={renderNotFound()}
        />
      </Form.Item>

      {selectedOption && (
        <>
          <Divider />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>PARENT PRODUCT:</Typography.Text>
              <Typography.Text code>{selectedOption.productName}</Typography.Text>
            </div>

            {attributeElements}
            {customFieldElements}

            {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>DEFAULT SELLING PRICE:</Typography.Text>
              <Typography.Text>{selectedOption.sellingPrice}</Typography.Text>
            </div> */}
          </div>
          <Divider />
        </>
      )}

      <Form.Item
        label="Unit Price"
        name="unitPrice"
        rules={[{ required: true }, { type: 'number', min: 0 }]}
      >
        <InputNumber disabled={isLoading} style={{ width: '100%' }} min={0} precision={2} />
      </Form.Item>

      <UnitDiscountFormItem disabled={isLoading} />
      <Form.Item
        label="Quantity"
        name="quantity"
        rules={[
          {
            required: true,
            type: 'integer',
            min: 1,
            message: 'Quantity must be a positive integer value',
          },
        ]}
      >
        <InputNumber disabled={isLoading} ref={quantityRef} style={{ width: '100%' }} />
      </Form.Item>

      <Space direction="vertical">
        <Text className={styles['stock__text']}>* Available Stock</Text>
        {renderedAvailableStock}
      </Space>

      <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
        <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
          Add
        </Button>
      </Form.Item>
    </Form>
  );
};
